import { useState } from 'react';
import { Plus, User, Users2, Heart, Baby } from 'lucide-react';
import { familyStorage, FamilyMember, FamilyRelationship } from '@/lib/familyStorage';
import { AddMemberForm } from './AddMemberForm';
import { useBrand } from '@/config/brands';

interface FamilyMemberPillsProps {
  selectedMemberId: string | null;
  onSelect: (memberId: string | null) => void;
}

const RELATIONSHIP_ICONS: Record<FamilyRelationship, typeof User> = {
  self: User,
  spouse: Heart,
  child: Baby,
  parent: Users2,
  sibling: Users2,
  other: User,
};

export function FamilyMemberPills({ selectedMemberId, onSelect }: FamilyMemberPillsProps) {
  const brand = useBrand();
  const isAhp = brand.design === 'civic';
  const [members, setMembers] = useState<FamilyMember[]>(() => familyStorage.getMembers());
  const [showAddForm, setShowAddForm] = useState(false);

  const handleMemberAdded = (member: FamilyMember) => {
    setMembers(familyStorage.getMembers());
    onSelect(member.id);
    setShowAddForm(false);
  };

  const activeBg = isAhp
    ? 'bg-civic-navy text-white border-civic-navy'
    : 'bg-matrix-green/20 text-matrix-green border-matrix-green';
  const inactiveBg = isAhp
    ? 'bg-gray-100 text-gray-600 border-gray-200 hover:border-gray-300'
    : 'bg-black/5 text-foreground/70 border-foreground/10 hover:border-foreground/20';

  return (
    <div className="space-y-2">
      <div className="flex items-center flex-wrap gap-2">
        {members.map(member => {
          const Icon = RELATIONSHIP_ICONS[member.relationship];
          const isSelected = selectedMemberId === member.id;

          return (
            <button
              key={member.id}
              type="button"
              onClick={() => onSelect(isSelected ? null : member.id)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-all ${
                isSelected ? activeBg : inactiveBg
              }`}
            >
              <Icon className="w-3 h-3" />
              {familyStorage.getDisplayName(member)}
            </button>
          );
        })}

        {!showAddForm && (
          <button
            type="button"
            onClick={() => setShowAddForm(true)}
            className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-dashed text-xs transition-all ${
              isAhp
                ? 'border-gray-300 text-gray-500 hover:border-civic-navy hover:text-civic-navy'
                : 'border-foreground/20 text-foreground/50 hover:border-matrix-green hover:text-matrix-green'
            }`}
          >
            <Plus className="w-3 h-3" />
            Add
          </button>
        )}
      </div>

      {showAddForm && (
        <AddMemberForm
          compact
          onAdded={handleMemberAdded}
          onCancel={() => setShowAddForm(false)}
        />
      )}
    </div>
  );
}
